// src/pages/PftImportsPage.js
//
// "PFT Uploads" (coordinator side, flag PFT_IMPORT_ENABLED): the patients found in
// uploaded PFT reports, one card per patient with every PFT date they have. A person
// checks each card (anything the extraction was unsure about is highlighted and must be
// ticked), corrects values if needed, and then moves it into Lung Center Referrals.
// Data: pft_imports (pftImportService.js); cards: pftImportGroups.js.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, Search, X, EyeOff, RotateCcw, Save, ExternalLink, Crosshair, Download, MessageSquare } from "lucide-react";
import { auth } from "../firebase";
import { useTenant } from "../context/TenantContext";
import { getActiveSiteId, getActiveTenantId } from "../services/activeTenant";
import { PFT_SURVEY_TEXT_ENABLED, UI_REVAMP_ENABLED } from "../config/featureFlags";
import {
  SURVEY_BOXES,
  cardInSurveyBox,
  moveWordingForBox,
  formatSentAt,
  planTextBatch,
  surveyStateOf,
  textBatchSummary,
  textBlockReason,
} from "../services/pftSurvey";
import { sendPftSurveyTexts } from "../services/pftSurveyService";
import { groupImportsIntoCards, needsReviewCards, pageOfList, readyToMoveCards, settleDobFromRegx } from "../services/pftImportGroups";
import { loadMatchCandidates } from "../services/pftMoveService";
import { PFT_TEST_FIELDS, PFT_TEST_VALUE_KEYS } from "../services/pftTests";
import { normalizeMrn9 } from "../services/mrn";
import {
  recordExport,
  separateImports,
  setDismissed,
  setIdentityChoice,
  setReviewed,
  setSoftCheckVerified,
  savePatientEdits,
  saveTestEdits,
  watchPftImports,
} from "../services/pftImportService";
import { openPftPdf } from "../services/pftPdfService";
import { reviewBoxesForPatient, reviewBoxesForTest, reviewTargets } from "../services/pftReviewTargets";
import { describeReviewItem } from "../services/pftReviewText";
import { withVerification } from "../services/pftSoftChecks";
import PftHistoryEditor from "../components/patient/PftHistoryEditor";
import PftMoveFlow from "./PftMoveFlow";
import PftMoveAll from "./PftMoveAll";
import PftFilterPanel from "./PftFilterPanel";
import PftSurveyAnswers from "./PftSurveyAnswers";
import {
  cardMatchesFilters,
  filterFieldLabel,
  hasActiveFilters,
  isActiveRule,
  newestTestOf,
  planFilteredMove,
} from "../services/pftCardFilter";
import { downloadCsv, exportFields, exportFileName, filteredExportRows, toCsv } from "../services/pftExport";

// "Moved to RegX" holds both ways a card left New (one tab since 2026-10-20): added as a
// new patient (status "moved") or merged into one already there ("merged"), with a filter.
const SECTIONS = [
  { key: "new", label: "New", statuses: ["new"] },
  { key: "moved", label: "Moved to RegX", statuses: ["moved", "merged"] },
  { key: "dismissed", label: "Dismissed", statuses: ["dismissed"] },
];
const MOVED_VIEWS = [
  { key: "all", label: "All" },
  { key: "moved", label: "Added as new" },
  { key: "merged", label: "Merged" },
];
const PATIENT_LABELS = {
  name: "Name", dob: "Date of birth", mrn: "MRN", sex: "Sex", phone: "Phone", address: "Address",
};
const SPECIAL_LABELS = {
  identity: "Same patient?",
  patients: "Several patients in one PDF",
  report: "Report found by one reading only",
  date: "PFT date",
};
const fieldLabel = (f) =>
  SPECIAL_LABELS[f] ||
  PATIENT_LABELS[f] ||
  (PFT_TEST_FIELDS.find((x) => x.key === f) || {}).label ||
  (/^check\d+$/.test(f) ? "Automatic check" : f);

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";
const labelCls = "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";
const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";

// Health-survey state on a card (flag PFT_SURVEY_TEXT_ENABLED): green = answered,
// orange = texted, no answer. Own classes so light mode can't wash them out.
const SURVEY_CARD_CLASS = { responded: "pft-survey-responded", texted: "pft-survey-texted" };
const SURVEY_CHIP = {
  responded: { cls: "pft-survey-chip-responded", text: "Survey answered" },
  texted: { cls: "pft-survey-chip-texted", text: "Texted, no answer" },
};

const me = () => (auth.currentUser && auth.currentUser.email) || "unknown";
const blank = (v) => v === undefined || v === null || String(v).trim() === "";
const valuesOf = (t) => Object.fromEntries(PFT_TEST_VALUE_KEYS.map((k) => [k, t[k] ?? ""]));

export default function PftImportsPage() {
  const navigate = useNavigate();
  // Opened from the admin Dashboard: Back returns there; otherwise to the Coordinator page.
  const fromWhere = useLocation()?.state?.from;
  const fromDashboard = fromWhere === "dashboard";
  // UI revamp: opened from the personal home page, so Back goes there.
  const fromHome = UI_REVAMP_ENABLED && fromWhere === "home";
  const tenant = useTenant() || {};
  const siteId = tenant.siteId || getActiveSiteId();
  const tenantId = tenant.tenantId || getActiveTenantId();
  const siteName = ((tenant.sitesMeta || []).find((s) => s.id === siteId) || {}).name || siteId;
  // Export of the filtered list: admins of this site only (decided 2026-10-19), the
  // same admin test as the Coordinator page.
  const isAdmin =
    !!tenant.isOwner ||
    (typeof tenant.isTenantAdmin === "function" && !!tenant.isTenantAdmin(tenant.tenantId)) ||
    tenant.roleForActiveSite === "admin";

  const [imports, setImports] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState("new");
  const [movedView, setMovedView] = useState("all"); // Moved to RegX: all | moved | merged
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all"); // New tab: all | review (needs review) | ready (ready to move)
  const [page, setPage] = useState(1);
  // "Filter by PFT values": no rules = no filter, so the page is as before.
  const [rules, setRules] = useState([]);
  const [matchMode, setMatchMode] = useState("all"); // all | any
  const [excluded, setExcluded] = useState(() => new Set()); // cards unticked for "Move filtered"
  const [openKey, setOpenKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [exportError, setExportError] = useState(null);
  // Health-survey texts (flag PFT_SURVEY_TEXT_ENABLED). Their own ticks: unticking for
  // a text never changes who "Move filtered" moves, and the other way round.
  const surveyOn = PFT_SURVEY_TEXT_ENABLED;
  const [surveyBox, setSurveyBox] = useState("all");
  const [untexted, setUntexted] = useState(() => new Set()); // cards unticked for "Text filtered"
  const [texting, setTexting] = useState(false);
  const [textError, setTextError] = useState(null);

  useEffect(
    () =>
      watchPftImports(
        (list) => {
          setImports(list);
          setLoadError(false);
        },
        () => setLoadError(true),
      ),
    [siteId],
  );

  // RegX's patients (consents + Sheet at Dubois), loaded once in the background: a card
  // with no DOB on any PDF whose MRN AND name match one of them needs no DOB check
  // (settleDobFromRegx, 2026-10-27). If they can't be loaded, the checks simply stay.
  const [regxPatients, setRegxPatients] = useState(null);
  useEffect(() => {
    let live = true;
    setRegxPatients(null);
    Promise.resolve()
      .then(() => loadMatchCandidates())
      .then((list) => live && setRegxPatients(list))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [siteId]);
  // Two steps, so RegX finishing loading replaces only the cards the DOB rule changes (an
  // open card keeps its object, and so its state).
  const groupedCards = useMemo(() => groupImportsIntoCards(imports || []), [imports]);
  const cards = useMemo(() => settleDobFromRegx(groupedCards, regxPatients), [groupedCards, regxPatients]);
  const counts = Object.fromEntries(SECTIONS.map((s) => [s.key, cards.filter((c) => s.statuses.includes(c.status)).length]));
  const q = search.trim().toLowerCase();
  const section = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];
  const sectionCards = cards.filter((c) => section.statuses.includes(c.status));
  const tabCards = tab === "moved" && movedView !== "all" ? sectionCards.filter((c) => c.status === movedView) : sectionCards;
  const filtersOn = hasActiveFilters(rules);
  const activeRules = rules.filter(isActiveRule);
  const matching = filtersOn ? tabCards.filter((c) => cardMatchesFilters(c, rules, matchMode)) : tabCards;
  // The survey box narrows the list further (All = as before).
  const box = surveyOn ? surveyBox : "all";
  const inTab = box === "all" ? matching : matching.filter((c) => cardInSurveyBox(c, box));
  const reviewCards = needsReviewCards(inTab);
  const readyCards = readyToMoveCards(inTab);
  const activeView = tab === "new" ? view : "all";
  const filtered = activeView === "review" ? reviewCards : activeView === "ready" ? readyCards : inTab;
  const shown = filtered
    .filter((c) => !q || [c.name, c.mrn9, c.mrnRaw, c.dob].some((v) => String(v || "").toLowerCase().includes(q)))
    .sort((a, b) => Number(b.needsReview) - Number(a.needsReview));
  const paged = pageOfList(shown, page);
  // A different tab, search or filter starts again at page 1.
  const filterKey = JSON.stringify([activeRules, matchMode]);
  useEffect(() => setPage(1), [tab, q, activeView, filterKey, movedView, box]);
  // Clearing the filters forgets who was left out.
  useEffect(() => {
    if (!filtersOn) {
      setExcluded(new Set());
      setUntexted(new Set());
    }
  }, [filtersOn]);
  const selectable = filtersOn && tab === "new";
  // "Move filtered": every result (all pages) that's ready and not left out.
  const filteredMove = planFilteredMove(selectable ? shown : [], excluded);
  const filteredNote = [
    `${filteredMove.move.length} matching patient(s) are ready to move.`,
    filteredMove.review.length ? `${filteredMove.review.length} still need review and will stay here.` : "",
    filteredMove.leftOut.length ? `${filteredMove.leftOut.length} you left out will stay here.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const toggleIncluded = (key) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const open = cards.find((c) => c.key === openKey) || null;

  // "Text filtered": every result (all pages) that can be texted and is still ticked.
  const textPlan = surveyOn && selectable ? planTextBatch(shown, untexted) : null;
  const toggleTexted = (key) =>
    setUntexted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const textFiltered = async () => {
    setTextError(null);
    if (!textPlan || !textPlan.send.length) return;
    if (!window.confirm(textBatchSummary(textPlan))) return;
    setTexting(true);
    try {
      const r = await sendPftSurveyTexts({ tenantId, siteId, cards: textPlan.send });
      setNotice(
        [
          `Texted ${r.sent} patient(s).`,
          r.skipped ? `${r.skipped} skipped (changed since the page loaded).` : "",
          r.failed ? `${r.failed} couldn't be sent; try those again.` : "",
        ]
          .filter(Boolean)
          .join(" "),
      );
    } catch (e) {
      setTextError(e.message);
    } finally {
      setTexting(false);
    }
  };
  // With a survey box picked, the Move buttons move only the cards in that box.
  const moveCards = (list) => (box === "all" ? list : list.filter((c) => cardInSurveyBox(c, box)));
  const boxNote =
    box === "all" ? undefined : `Only patients in "${(SURVEY_BOXES.find((b) => b.key === box) || {}).label}".`;

  // Every result on every page (search and view included). The export is recorded on
  // each exported import first; if that fails, nothing is downloaded.
  const exportFiltered = async () => {
    setExportError(null);
    if (!window.confirm(`Download ${shown.length} patient(s)' name, DOB and MRN? This is logged.`)) return;
    try {
      await recordExport(
        shown.flatMap((c) => (c.imports || []).map((i) => i.id)),
        me(),
        { rows: shown.length, fields: exportFields(rules) },
      );
    } catch (e) {
      setExportError("Couldn't record the export, so nothing was downloaded. Try again.");
      return;
    }
    downloadCsv(exportFileName(siteId), toCsv(filteredExportRows(shown, rules)));
    setNotice(`Exported ${shown.length} patient(s).`);
  };

  return (
    <div className="min-h-screen bg-[#0b0820] text-slate-100 px-4 py-6 sm:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button
            onClick={() => navigate(fromHome ? "/home" : fromDashboard ? "/dashboard" : "/coordinator")}
            className={`${btn} px-3 py-2 rounded-xl text-sm`}
          >
            <ArrowLeft className="w-4 h-4" />
            {fromHome ? "Home" : fromDashboard ? "Dashboard" : "Coordinator"}
          </button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Review PFT Patients</h1>
            <p className="text-[12px] text-slate-400">
              Patients found in uploaded PFT reports. Check each one, then move it into Lung Center Referrals.
            </p>
            <p className="text-[12px] text-slate-300 mt-0.5">
              Site: <span className="font-semibold">{siteName}</span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3" role="tablist">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={tab === s.key}
              onClick={() => setTab(s.key)}
              className={`px-3 py-1.5 rounded-lg border text-[12px] transition ${
                tab === s.key
                  ? "bg-purple-500/20 border-purple-300/40 text-white"
                  : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08]"
              }`}
            >
              {s.label} ({counts[s.key]})
            </button>
          ))}
          {tab === "moved" && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 p-0.5" role="group" aria-label="Show moved">
              {MOVED_VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  aria-pressed={movedView === v.key}
                  onClick={() => setMovedView(v.key)}
                  className={`px-2.5 py-1 rounded-md text-[12px] transition ${
                    movedView === v.key ? "bg-purple-500/20 text-white" : "text-slate-300 hover:bg-white/[0.08]"
                  }`}
                >
                  {v.label} ({v.key === "all" ? sectionCards.length : sectionCards.filter((c) => c.status === v.key).length})
                </button>
              ))}
            </div>
          )}
          {tab === "new" && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-white/10 p-0.5" role="group" aria-label="Show">
              {[
                { key: "all", label: "All", n: inTab.length, on: "bg-purple-500/20 text-white" },
                { key: "review", label: "Needs review", n: reviewCards.length, on: "bg-amber-500/15 text-amber-200", icon: true },
                { key: "ready", label: "Ready to move", n: readyCards.length, on: "bg-emerald-500/15 text-emerald-200" },
              ].map((v) => (
                <button
                  key={v.key}
                  type="button"
                  aria-pressed={view === v.key}
                  onClick={() => setView(v.key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] transition ${
                    view === v.key ? v.on : "text-slate-300 hover:bg-white/[0.08]"
                  }`}
                >
                  {v.icon && <AlertTriangle className="w-3.5 h-3.5" />}
                  {v.label} ({v.n})
                </button>
              ))}
            </div>
          )}
          {/* Move all: every New card, exactly as before. On "Ready to move" it gets only
              those cards. Hidden on "Needs review", where moving everything would surprise.
              With PFT filters on, "Move filtered" replaces both: only the matching, ready,
              still-ticked cards. */}
          {tab === "new" && !filtersOn && view === "all" && (
            <PftMoveAll cards={moveCards(cards)} onDone={setNotice} note={boxNote} wording={moveWordingForBox(box)} />
          )}
          {tab === "new" && !filtersOn && view === "ready" && (
            <PftMoveAll cards={readyCards} onDone={setNotice} scope="ready" note={boxNote} wording={moveWordingForBox(box, "ready")} />
          )}
          {selectable && view !== "review" && (
            <PftMoveAll
              cards={filteredMove.move}
              onDone={setNotice}
              scope="filtered"
              note={boxNote ? `${boxNote} ${filteredNote}` : filteredNote}
              wording={moveWordingForBox(box, "filtered")}
            />
          )}
          {textPlan && (
            <button onClick={textFiltered} disabled={!textPlan.send.length || texting} className={btn}>
              <MessageSquare className="w-3.5 h-3.5" />
              {texting ? "Texting…" : `Text filtered (${textPlan.send.length})`}
            </button>
          )}
          {filtersOn && isAdmin && (
            <button onClick={exportFiltered} disabled={!shown.length} className={btn}>
              <Download className="w-3.5 h-3.5" />
              Export ({shown.length})
            </button>
          )}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, MRN or DOB"
              aria-label="Search name, MRN or DOB"
              className={`${inputCls} pl-9`}
            />
          </div>
        </div>

        {surveyOn && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[12px] text-slate-400">Health survey:</span>
            <div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-white/10 p-0.5" role="group" aria-label="Health survey">
              {SURVEY_BOXES.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  aria-pressed={surveyBox === b.key}
                  onClick={() => setSurveyBox(b.key)}
                  className={`px-2.5 py-1 rounded-md text-[12px] transition ${
                    surveyBox === b.key ? "bg-purple-500/20 text-white" : "text-slate-300 hover:bg-white/[0.08]"
                  }`}
                >
                  {b.label} ({matching.filter((c) => cardInSurveyBox(c, b.key)).length})
                </button>
              ))}
            </div>
          </div>
        )}

        <PftFilterPanel
          rules={rules}
          setRules={setRules}
          mode={matchMode}
          setMode={setMatchMode}
          matchCount={matching.length}
          totalCount={tabCards.length}
        />
        {textPlan && untexted.size > 0 && (
          <p className="mb-3 text-[12px] text-slate-400 flex items-center gap-2">
            {untexted.size} patient(s) unticked for "Text filtered".
            <button type="button" onClick={() => setUntexted(new Set())} className={btn}>
              Tick all again
            </button>
          </p>
        )}
        {textError && <p className="mb-3 text-[13px] text-amber-200">{textError}</p>}
        {selectable && excluded.size > 0 && (
          <p className="mb-3 text-[12px] text-slate-400 flex items-center gap-2">
            {excluded.size} patient(s) left out of "Move filtered".
            <button type="button" onClick={() => setExcluded(new Set())} className={btn}>
              Include all again
            </button>
          </p>
        )}

        {notice && (
          <p className="mb-3 text-[13px] text-emerald-200 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" />
            {notice}
          </p>
        )}
        {exportError && <p className="mb-3 text-[13px] text-amber-200">{exportError}</p>}
        {loadError ? (
          <p className="text-sm text-amber-200">Couldn't load the PFT uploads. Try reloading the page.</p>
        ) : imports === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-slate-400">
            {q
              ? "No patients here match that search."
              : box !== "all" && matching.length
                ? "No patients in this survey box."
              : filtersOn
                ? "No patients match these filters."
              : activeView === "review"
                ? "No patients need review."
                : activeView === "ready"
                  ? "No patients are ready to move. Tick each card's review items first."
                  : "No patients here."}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {paged.items.map((c) => {
                const survey = surveyOn ? surveyStateOf(c) : "none";
                const textReason = textPlan ? textBlockReason(c) : "";
                return (
                <div key={c.key} className="relative">
                <button
                  onClick={() => setOpenKey(c.key)}
                  className={`w-full h-full text-left rounded-2xl border p-4 bg-white/[0.03] hover:bg-white/[0.06] transition ${
                    SURVEY_CARD_CLASS[survey]
                      ? SURVEY_CARD_CLASS[survey]
                      : c.needsReview && c.status === "new"
                        ? "border-amber-300/50 ring-1 ring-amber-300/40"
                        : c.oneReadingToRecheck && c.status === "new"
                          ? "pft-one-reading-card"
                          : "border-white/10"
                  } ${selectable ? (textPlan ? "pb-16" : "pb-11") : ""}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-sm text-white break-words">{c.name || "(no name)"}</span>
                    {c.needsReview && c.status === "new" && (
                      <span className="ml-auto shrink-0 text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/10 border-amber-400/30 text-amber-200">
                        Needs review
                      </span>
                    )}
                    {!c.needsReview && c.status === "new" && c.oneReadingToRecheck > 0 && (
                      <span className="ml-auto shrink-0 text-[10px] px-2 py-0.5 rounded-full border pft-one-reading-chip">
                        {c.oneReadingToRecheck} to recheck
                      </span>
                    )}
                    {(c.status === "moved" || c.status === "merged") && (
                      <span className="ml-auto shrink-0 text-[10px] px-2 py-0.5 rounded-full border bg-white/[0.04] border-white/10 text-slate-300">
                        {c.status === "merged" ? "Merged" : "Added as new"}
                      </span>
                    )}
                  </div>
                  {SURVEY_CHIP[survey] && (
                    <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border ${SURVEY_CHIP[survey].cls}`}>
                      {SURVEY_CHIP[survey].text}
                    </span>
                  )}
                  <p className="text-[12px] text-slate-400 mt-1">
                    DOB {c.dob || "—"} · MRN {c.mrn9 || c.mrnRaw || "—"}
                  </p>
                  <p className="text-[12px] text-slate-300 mt-1">
                    {c.tests.length} PFT date{c.tests.length === 1 ? "" : "s"}
                    {c.tests[0] && c.tests[0].date ? ` · newest ${c.tests[0].date}` : ""}
                  </p>
                  {c.needsReview && c.status === "new" && (
                    <p className="text-[11px] text-amber-200 mt-1">
                      {c.review.filter((r) => !r.resolved).length} item(s) to check
                      {c.review.some((r) => r.field === "identity" && !r.resolved) ? " · name or DOB differs between PDFs" : ""}
                    </p>
                  )}
                  {filtersOn && (
                    <p className="text-[11px] text-purple-200 mt-1">
                      {[...new Set(activeRules.map((r) => r.field))]
                        .map((k) => `${filterFieldLabel(k)}: ${(newestTestOf(c) || {})[k] || "—"}`)
                        .join(" · ")}
                    </p>
                  )}
                </button>
                {selectable &&
                  (c.needsReview ? (
                    <span className="absolute bottom-3 left-4 text-[11px] text-amber-200">Needs review: won't be moved</span>
                  ) : (
                    <label className="absolute bottom-3 left-4 inline-flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!excluded.has(c.key)}
                        onChange={() => toggleIncluded(c.key)}
                        className="accent-purple-500"
                      />
                      Include in "Move filtered"
                    </label>
                  ))}
                {textPlan &&
                  (survey !== "none" ? null : textReason ? (
                    <span className="absolute bottom-8 left-4 text-[11px] text-slate-400">Can't text: {textReason}</span>
                  ) : (
                    <label className="absolute bottom-8 left-4 inline-flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!untexted.has(c.key)}
                        onChange={() => toggleTexted(c.key)}
                        className="accent-purple-500"
                      />
                      Include in "Text filtered"
                    </label>
                  ))}
                </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <span className="text-[12px] text-slate-400">
                Showing {paged.start}–{paged.end} of {paged.total}
              </span>
              {paged.pageCount > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage(paged.page - 1)}
                    disabled={paged.page <= 1}
                    className={`${btn} disabled:cursor-not-allowed`}
                  >
                    Previous
                  </button>
                  <span className="text-[12px] text-slate-400 px-1">
                    Page {paged.page} of {paged.pageCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage(paged.page + 1)}
                    disabled={paged.page >= paged.pageCount}
                    className={`${btn} disabled:cursor-not-allowed`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {open && (
        <CardDetail
          card={open}
          tenantId={tenantId}
          siteId={siteId}
          surveyOn={surveyOn}
          onClose={() => setOpenKey(null)}
          onMoved={(text) => {
            setOpenKey(null);
            setNotice(text);
          }}
        />
      )}
    </div>
  );
}

function CardDetail({ card, tenantId, siteId, surveyOn, onClose, onMoved }) {
  const pending = card.status === "new" ? card.pendingImports : card.imports.filter((i) => i.status === card.status);
  const baseTests = useMemo(() => card.tests.map((t) => ({ ...t })), [card]);
  const [tests, setTests] = useState(baseTests);
  const [patient, setPatient] = useState({
    name: card.name || "", dob: card.dob || "", mrn: card.mrnRaw || card.mrn9 || "",
    sex: card.sex || "", phone: card.phone || "", address: card.address || "",
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const editable = card.status === "new";

  // Re-sync with the live data (another person's edit, a tick) only while there are no
  // unsaved edits here: a live update must never wipe what someone has typed.
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!dirtyRef.current) setTests(baseTests);
  }, [baseTests]);

  const changedTests = tests.filter((t) => {
    const before = baseTests.find((b) => b.id === t.id);
    return before && JSON.stringify(valuesOf(before)) !== JSON.stringify(valuesOf(t));
  });
  const patientChanges = Object.fromEntries(
    Object.entries(patient).filter(([k, v]) => {
      const before = { name: card.name, dob: card.dob, mrn: card.mrnRaw || card.mrn9, sex: card.sex, phone: card.phone, address: card.address }[k];
      return String(v || "") !== String(before || "");
    }),
  );
  const dirty = changedTests.length > 0 || Object.keys(patientChanges).length > 0;
  dirtyRef.current = dirty;

  // Closing with unsaved edits asks first (X, Escape); leaving or reloading the page too.
  const requestClose = () => {
    if (dirty && !window.confirm("You have unsaved changes on this card. Close without saving them?")) return;
    onClose();
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const open = card.review.filter((r) => !r.resolved && (r.importId === null || pending.some((p) => p.id === r.importId)));
  // The boxes the unticked items are about are outlined (decided 2026-10-12), and "Show"
  // on an item jumps to its box.
  // Each outlined box says, under it, exactly what to check (describeReviewItem).
  const reviewBoxes = (t) => new Map([...reviewBoxesForTest(t, open)].map(([k, r]) => [k, describeReviewItem(r).text]));
  const patientBoxes = new Map([...reviewBoxesForPatient(open)].map(([k, r]) => [k, describeReviewItem(r).text]));
  const [focus, setFocus] = useState(null);
  const testOfItem = (r) =>
    tests.find((t) => t.importId === r.importId || (t.alsoIn || []).some((a) => a.importId === r.importId));
  const canShow = (r) => {
    const tg = reviewTargets(r);
    return (tg.test.length > 0 && !!testOfItem(r)) || tg.patient.length > 0;
  };
  const show = (r) => {
    const tg = reviewTargets(r);
    const t = tg.test.length ? testOfItem(r) : null;
    if (t) {
      setFocus({ testId: t.id, key: tg.test[0], n: Date.now() });
      return;
    }
    const el = tg.patient.length ? document.querySelector(`[data-pft-box="patient:${tg.patient[0]}"]`) : null;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    if (el && el.focus) el.focus({ preventScroll: true });
  };

  const run = async (fn, ok) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (ok) setMessage({ tone: "ok", text: ok });
    } catch (e) {
      setMessage({ tone: "warn", text: "Couldn't save. Check your connection and try again." });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(async () => {
      for (const t of changedTests) {
        const imp = card.imports.find((i) => i.id === t.importId);
        const before = valuesOf(baseTests.find((b) => b.id === t.id));
        const after = valuesOf(t);
        const values = Object.fromEntries(Object.keys(after).filter((k) => after[k] !== before[k]).map((k) => [k, after[k]]));
        await saveTestEdits(imp, values, me());
      }
      if (Object.keys(patientChanges).length) {
        const values = { ...patientChanges };
        if ("mrn" in values) {
          const n = normalizeMrn9(values.mrn);
          values.mrn9 = n.mrn9;
          values.mrnIssue = n.issue;
        }
        await savePatientEdits(pending, values, me());
      }
    }, "Saved.");

  const tick = (item, checked) =>
    run(() => setReviewed(item.importId ? [item.importId] : pending.map((p) => p.id), item.field, me(), checked));

  // A soft check verified here (saved at once, like a review tick) comes along to RegX
  // already verified. Shown at once; the live update then confirms it.
  const verifySoftCheck = (t, key, checked) => {
    setTests((ts) => ts.map((x) => (x.id === t.id ? withVerification(x, key, { checked, by: me(), now: new Date().toISOString() }) : x)));
    run(() => setSoftCheckVerified(t.importId, key, me(), checked));
  };

  const dismiss = (d) => {
    if (d && !window.confirm("Dismiss this patient? It moves to Dismissed (nothing is deleted) and can be restored.")) return;
    run(() => setDismissed(pending.map((p) => p.id), me(), d), d ? "Dismissed." : "Restored to New.");
  };

  const confirmSame = (v) =>
    run(
      () => setIdentityChoice(pending.map((p) => p.id), { name: v.name, dob: v.dob }, me()),
      `Confirmed as one patient, using ${v.name || "that name"}.`,
    );

  const openOne = async (f) => {
    try {
      await openPftPdf(f.file);
    } catch (e) {
      setMessage({ tone: "warn", text: e.message });
    }
  };

  const separate = (v) => {
    if (!window.confirm(`Move ${v.files.map((f) => f.fileName).join(", ")} (${v.name || "no name"}) onto a separate card? Nothing is deleted.`)) return;
    run(() => separateImports(v.importIds, me()), `Separated: ${v.name || "that PDF"} now has its own card.`);
  };

  const fileOf = (importId) => (card.imports.find((i) => i.id === importId) || {}).fileName || "";

  // Health survey: text this one patient, or text again with the same link.
  const surveyState = surveyStateOf(card);
  const surveyBlock = surveyOn && card.status === "new" ? textBlockReason(card, { resend: true }) : "";
  const textOne = async () => {
    const s = card.survey || {};
    const ask =
      surveyState === "texted"
        ? `Text ${card.name || "this patient"} again? Last texted ${formatSentAt(s.lastSentAt) || "before"}${
            s.lastSentBy ? ` by ${s.lastSentBy}` : ""
          }. They get the same link.`
        : `Text ${card.name || "this patient"} the health survey link at ${card.phone}?`;
    if (!window.confirm(ask)) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await sendPftSurveyTexts({ tenantId, siteId, cards: [card], resend: surveyState === "texted" });
      const one = (r.results || [])[0] || {};
      setMessage(
        one.ok
          ? { tone: "ok", text: "Texted." }
          : { tone: "warn", text: one.reason ? `Not texted: ${one.reason}.` : "Couldn't send the text. Try again." },
      );
    } catch (e) {
      setMessage({ tone: "warn", text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-6" role="dialog" aria-modal="true" aria-label={card.name || "Patient card"}>
      <div className="w-full max-w-3xl max-h-full flex flex-col rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl pft-modal-panel">
        <div className="shrink-0 flex items-start gap-3 px-5 pt-4 pb-3 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold">{card.name || "(no name)"}</h2>
            <p className="text-[12px] text-slate-400">
              DOB {card.dob || (card.dobFromRegx ? `${card.dobFromRegx.dob} (from the RegX patient with this MRN and name)` : "—")} · MRN{" "}
              {card.mrn9 || card.mrnRaw || "—"} · {card.tests.length} PFT date
              {card.tests.length === 1 ? "" : "s"} from {new Set(card.imports.map((i) => i.file)).size} PDF
              {new Set(card.imports.map((i) => i.file)).size === 1 ? "" : "s"}
            </p>
            {(card.otherNamesPrinted || []).length > 0 && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Also printed as {card.otherNamesPrinted.join(" · ")} (only a middle name differs, so the fuller name is used).
              </p>
            )}
          </div>
          <button onClick={requestClose} className={`ml-auto ${btn}`} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">

        {(card.sharedPdfs || []).length > 0 && (
          <div className="rounded-lg border border-sky-300/30 bg-sky-500/10 px-3 py-2 text-[12px] text-sky-200">
            {card.sharedPdfs.map((s) => (
              <p key={s.file}>
                {s.fileName} also has {s.otherPatients} other patient{s.otherPatients === 1 ? "'s" : "s'"} report
                {s.otherPatients === 1 ? "" : "s"}. Each patient's card links the same PDF.
              </p>
            ))}
          </div>
        )}

        {(card.sameMrnCards || []).length > 0 && (
          <div className="rounded-lg border border-amber-300/40 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-100">
            <p className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Another card has the same MRN ({card.mrn9})
            </p>
            {card.sameMrnCards.map((o) => (
              <p key={o.key}>
                {o.name || "(no name)"} · {o.status === "new" ? "still in New" : o.status === "dismissed" ? "dismissed" : "already moved"}
              </p>
            ))}
            <p className="mt-1 text-amber-200">
              If they're different people, one MRN is probably wrong. Check the MRN on this PDF and correct it below before moving.
            </p>
          </div>
        )}

        {/* ---- the patient's health-survey answers ---- */}
        {surveyOn && card.survey && card.survey.status === "responded" && card.survey.submissionId && (
          <PftSurveyAnswers submissionId={card.survey.submissionId} card={card} />
        )}

        {/* ---- what needs checking ---- */}
        {card.review.length > 0 && (
          <div className={`rounded-xl border p-3 space-y-2 ${open.length ? "border-amber-300/40 bg-amber-500/10" : "border-emerald-300/30 bg-emerald-500/10"}`}>
            <p className={`text-[13px] font-medium flex items-center gap-1.5 ${open.length ? "text-amber-100" : "text-emerald-100"}`}>
              {open.length ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {open.length ? `${open.length} item(s) to check against the PDF` : "Everything has been checked"}
            </p>
            {card.review.map((r, i) => (
              <div key={`${r.importId}-${r.field}-${i}`} className="space-y-1.5">
                <label className="flex items-start gap-2 text-[12px] text-slate-100">
                  {r.field === "identity" || r.autoResolved ? (
                    r.resolved ? <CheckCircle2 className="mt-0.5 w-3.5 h-3.5 text-emerald-300 shrink-0" /> : <AlertTriangle className="mt-0.5 w-3.5 h-3.5 text-amber-300 shrink-0" />
                  ) : (
                  <input
                    type="checkbox"
                    checked={!!r.resolved}
                    disabled={!editable || busy}
                    onChange={(e) => tick(r, e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 accent-purple-500"
                  />
                  )}
                  <span>
                    {/* Which box, then what was found and what to check, in plain words. */}
                    <span className="font-semibold">{r.field === "identity" ? fieldLabel(r.field) : describeReviewItem(r).title}</span>
                    {": "}
                    {r.field === "identity" ? r.reason : r.autoResolved ? `settled automatically: ${r.autoResolved}` : describeReviewItem(r).text}
                    {r.importId && <span className="text-slate-400"> · {fileOf(r.importId)}</span>}
                  </span>
                  {!r.resolved && canShow(r) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        show(r);
                      }}
                      className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-200 transition"
                      title="Go to the highlighted box"
                    >
                      <Crosshair className="w-3 h-3" />
                      Show
                    </button>
                  )}
                </label>
                {r.variants && (
                  <div className="ml-6 space-y-1.5 text-[12px]">
                    <p className="text-slate-300">{r.why} Open the PDFs to compare.</p>
                    {r.resolved && r.chosen && (
                      <p className="text-emerald-200">
                        Confirmed as one patient, using {r.chosen.name || "(no name)"} · DOB {r.chosen.dob || "—"}. You can change this below.
                      </p>
                    )}
                    {r.variants.map((v) => (
                      <div key={v.importIds.join(",")} className="rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2 space-y-1.5">
                        <div className="text-slate-100">
                          <span className="font-semibold">{v.name || "(no name)"}</span> · DOB {v.dob || "—"}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {v.files.map((f) => (
                            <button
                              key={f.importId}
                              onClick={() => openOne(f)}
                              disabled={!f.file}
                              className={btn}
                              title="Open this PDF"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {f.fileName || "PDF"}
                              {f.pages ? ` · pages ${f.pages}` : ""}
                            </button>
                          ))}
                        </div>
                        {editable && r.variants.length > 1 && (
                          <div className="flex flex-wrap gap-1.5">
                            <button onClick={() => confirmSame(v)} disabled={busy} className={btn}>
                              Same patient: use this name and DOB
                            </button>
                            <button onClick={() => separate(v)} disabled={busy} className={btn} title="Move these PDFs onto a card of their own">
                              Not the same patient: separate
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ---- patient details ---- */}
        <div className="space-y-2">
          <span className="block text-[11px] font-semibold text-slate-200">Patient details (from the report)</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.keys(PATIENT_LABELS).map((k) => (
              <label key={k} className={`block ${k === "address" ? "sm:col-span-2" : ""}`}>
                <span className={labelCls}>{PATIENT_LABELS[k]}</span>
                <input
                  value={patient[k]}
                  disabled={!editable}
                  onChange={(e) => setPatient((p) => ({ ...p, [k]: e.target.value }))}
                  className={patientBoxes.has(k) ? `${inputCls} pft-review-box` : inputCls}
                  aria-label={PATIENT_LABELS[k]}
                  data-pft-box={`patient:${k}`}
                  aria-invalid={patientBoxes.has(k) || undefined}
                />
                {patientBoxes.has(k) && (
                  <span className="block text-[10px] text-amber-200 mt-0.5 font-medium">To check: {patientBoxes.get(k)}</span>
                )}
              </label>
            ))}
          </div>
          {(card.smokingStatus || card.smokingNote) && (
            <p className="text-[12px] text-slate-300">
              Smoking status: {card.smokingStatus || "not clear"}
              {card.smokingNote ? ` (report says: "${card.smokingNote}")` : ""}
            </p>
          )}
          {!blank(patient.mrn) && normalizeMrn9(patient.mrn).issue && (
            <p className="text-[12px] text-amber-200">MRN {normalizeMrn9(patient.mrn).issue}: check it against the PDF.</p>
          )}
        </div>

        {/* ---- PFT dates ---- */}
        <PftHistoryEditor
          tests={tests}
          onChange={editable ? setTests : () => {}}
          pftNA={false}
          naToggle={null}
          userEmail={me()}
          inputCls={inputCls}
          inputDateCls={`${inputCls} [color-scheme:dark]`}
          labelCls={labelCls}
          onError={(text) => setMessage({ tone: "warn", text })}
          fixedList
          startCollapsed
          patientName={card.name}
          intro="Every PFT date found for this patient, newest first. Open a PDF to check a value. Boxes outlined in amber still need checking."
          reviewBoxes={card.status === "new" ? reviewBoxes : null}
          focus={focus}
          softChecks
          patientAddress={card.address || ""}
          patientDob={card.dob || ""}
          onVerify={editable ? verifySoftCheck : null}
          softChecksLocked={!editable}
        />
        </div>

        {/* ---- actions: always visible ---- */}
        <div className="shrink-0 border-t border-white/10 px-5 py-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <button onClick={save} disabled={!dirty || busy} className={btn}>
              <Save className="w-3.5 h-3.5" />
              Save changes
            </button>
          )}
          {card.status === "new" && (
            <button onClick={() => dismiss(true)} disabled={busy} className={btn}>
              <EyeOff className="w-3.5 h-3.5" />
              Dismiss
            </button>
          )}
          {card.status === "dismissed" && (
            <button onClick={() => dismiss(false)} disabled={busy} className={btn}>
              <RotateCcw className="w-3.5 h-3.5" />
              Restore
            </button>
          )}
          {surveyOn && card.status === "new" && surveyState !== "responded" && (
            <button
              onClick={textOne}
              disabled={busy || dirty || !!surveyBlock}
              className={btn}
              title={dirty ? "Save your changes first" : surveyBlock ? `Can't text: ${surveyBlock}` : undefined}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {surveyState === "texted" ? "Text again" : "Text this patient"}
            </button>
          )}
          {card.status === "new" && (
            <PftMoveFlow
              card={card}
              blockedReason={dirty ? "Save your changes first" : busy ? "Saving…" : null}
              uncheckedCount={open.length}
              onDone={onMoved}
            />
          )}
        </div>
        {surveyOn && (
          <p className="text-[12px] text-slate-300 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" />
            {surveyState === "responded"
              ? "Health survey: answered."
              : surveyState === "texted"
                ? `Health survey: texted ${formatSentAt(card.survey.lastSentAt)}${
                    card.survey.lastSentBy ? ` by ${card.survey.lastSentBy}` : ""
                  }${card.survey.sendCount > 1 ? ` (${card.survey.sendCount} times)` : ""}, no answer yet.`
                : "Health survey: not texted."}
            {card.status === "new" && surveyState !== "responded" && surveyBlock && (
              <span className="text-slate-400"> Can't text: {surveyBlock}.</span>
            )}
          </p>
        )}
        {dirty && editable && (
          <p className="text-[12px] text-amber-200 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            You have unsaved changes. Save them before moving this patient or closing the card.
          </p>
        )}
        {card.status === "new" && open.length > 0 && !dirty && (
          <p className="text-[11px] text-amber-200">
            {open.length} item(s) haven't been checked yet. You can still move this patient; you'll be asked to confirm.
          </p>
        )}
        {message && (
          <p className={`text-[12px] ${message.tone === "warn" ? "text-amber-200" : "text-emerald-200"}`}>{message.text}</p>
        )}
        </div>
      </div>
    </div>
  );
}

